import { useEffect, useState } from 'react';
import { apiExt, isUnsupported, type WebhookDelivery } from '../../lib/api';
import { t } from '../../lib/i18n';
import { sx } from './styles';

export function WebhookDeliveriesPanel() {
  const [items, setItems] = useState<WebhookDelivery[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [unsupported, setUnsupported] = useState(false);

  async function load(after?: string) {
    setLoading(true);
    try {
      const r = await apiExt.listWebhookDeliveries(50, after);
      if (isUnsupported(r)) {
        setUnsupported(true);
        setItems([]);
        setCursor(null);
      } else {
        setUnsupported(false);
        setItems((cur) => (after ? [...cur, ...r.items] : r.items));
        setCursor(r.nextCursor);
      }
    } catch (e) {
      alert(`Load failed: ${e instanceof Error ? e.message : e}`);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <section>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <h2 style={{ margin: 0, fontSize: 18 }}>{t('settings.webhookDeliveries.title')}</h2>
        <button onClick={() => load()} style={sx.btnSecondary}>
          {t('common.refresh')}
        </button>
      </div>
      {unsupported && <div style={sx.hint}>{t('settings.security.backendMissing')}</div>}
      {loading && <div>{t('common.loading')}</div>}
      <table style={sx.table}>
        <thead>
          <tr>
            <th style={sx.th}>{t('settings.webhookDeliveries.time')}</th>
            <th style={sx.th}>{t('settings.webhookDeliveries.url')}</th>
            <th style={sx.th}>{t('settings.webhookDeliveries.event')}</th>
            <th style={sx.th}>{t('settings.webhookDeliveries.status')}</th>
            <th style={sx.th}>{t('settings.webhookDeliveries.code')}</th>
            <th style={sx.th}>{t('settings.webhookDeliveries.duration')}</th>
          </tr>
        </thead>
        <tbody>
          {items.length === 0 && !loading && (
            <tr>
              <td colSpan={6} style={{ ...sx.td, color: '#9ca3af' }}>
                {t('common.noData')}
              </td>
            </tr>
          )}
          {items.map((d) => {
            const statusColor =
              d.status === 'success' ? '#10b981' : d.status === 'failed' ? '#dc2626' : '#f59e0b';
            return (
              <tr key={d.id}>
                <td style={sx.td}>{new Date(d.attemptedAt).toLocaleString()}</td>
                <td style={{ ...sx.td, fontFamily: 'monospace', fontSize: 11 }}>{d.url}</td>
                <td style={sx.td}>{d.event}</td>
                <td style={sx.td}>
                  <span style={sx.badge(statusColor)}>{d.status}</span>
                </td>
                <td style={sx.td}>{d.httpCode ?? '—'}</td>
                <td style={sx.td}>{d.durationMs ?? '—'}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {cursor && (
        <button onClick={() => load(cursor)} style={{ ...sx.btnSecondary, marginTop: 12 }}>
          {t('settings.webhookDeliveries.loadMore')}
        </button>
      )}
    </section>
  );
}
